import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';

import { UtilityService } from '@core/services/utility.service';
import { isEmptyValue } from '@core/helpers/value-empty-checker.helper';
import { BaseFormControlDirective } from '@pages/office/office-form/office-form/form-controls/base-form-control.directive';
import { ControlWrapperComponent } from '../control-wrapper/control-wrapper.component';
import { DATE_PICKER_CONFIG } from './datepicker.constant';
import { FormHelper } from '../../../../shared/helpers/form.helper';
import { DateTimeHelper } from '../../../../shared/helpers/date-time.helper';
import { ControlHasRequiredPipe } from '../pipes/control-has-required.pipe';
import { DatepickerValidator } from '../../validators/datepicker.validator';

export enum DatepickerTypeEnum {
  date = 'date',
  month = 'month',
}

@Component({
    selector: 'app-datepicker',
    templateUrl: 'datepicker.component.html',
    styleUrls: ['datepicker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ControlWrapperComponent, ReactiveFormsModule, NgClass, AsyncPipe, BsDatepickerModule, ControlHasRequiredPipe]
})
export class DatepickerComponent extends BaseFormControlDirective implements OnInit {
  @Input() type: `${DatepickerTypeEnum}` = 'date';

  public datePickerOptions: Partial<BsDatepickerConfig>;
  public innerControl = new FormControl<string | Date>('', { updateOn: 'blur' });

  private utilityService = inject(UtilityService);

  ngOnInit(): void {
    super.ngOnInit();
    this.setDatepickerOptions();
    this.setValue(this.control.value);
    this.onControlValueChanges();
    this.onInnerControlValueChanges();
    this.forwardErrorsToInnerControl();
    this.isTouchedInnerControlStream();
  }

  private setDatepickerOptions(): void {
    const hasMask = this.officeField?.InputMask?.toUpperCase();
    const dateFormat = hasMask || this.utilityService.getDateFormat();
    this.datePickerOptions = {
      ...DATE_PICKER_CONFIG(dateFormat, this.type !== DatepickerTypeEnum.date),
    };
  }

  private onChange(value: string | Date): void {
    this.control.markAsTouched();
    if (!value) {
      !isEmptyValue(this.control.value) && this.control.markAsDirty();
      this.control.setValue(null);
      return;
    }
    // NOTE: Invalid value is an object Invalid Date {}
    if (value && DateTimeHelper.isInvalidDate(value?.toString())) {
      this.innerControl.setValue(value, { emitEvent: false });
      this.control.setValue(null, { emitEvent: false });
      this.control.setErrors(DatepickerValidator.invalidDateErrorMessage(true));
      return;
    } else if (this.control.invalid) {
      this.control.setErrors(DatepickerValidator.invalidDateErrorMessage(false));
    }

    this.control.markAsDirty();
    this.control.setValue(DateTimeHelper.getDBValue(value));
  }

  private setValue(value: string): void {
    if (!value) {
      this.innerControl.setValue('', { emitEvent: false });
      return;
    }

    const parsedValue = DateTimeHelper.isInvalidDate(value)
      ? DateTimeHelper.getJSDateFromDotNetDate(value)
      : DateTimeHelper.getJSDateFromFormattedString(value);

    if (!DateTimeHelper.isSameDate(this.innerControl.value, parsedValue)) {
      this.innerControl.setValue(parsedValue, { emitEvent: false });
    }
  }

  private onControlValueChanges(): void {
    this.control.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(value => this.setValue(value));
  }

  private onInnerControlValueChanges(): void {
    this.innerControl.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(value => this.onChange(value));
  }

  private forwardErrorsToInnerControl(): void {
    combineLatest([FormHelper.getControlInvalidStatus(this.control), FormHelper.getControlDisabledStatus(this.control), this.isTouched$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isInvalid, isDisabled, isTouched]) => {
        isTouched && this.innerControl.setErrors(DatepickerValidator.forwardedErrorMessage(isInvalid));
        if (isDisabled) {
          const { value } = this.innerControl;
          !!value && DateTimeHelper.isInvalidDate(value.toString()) && this.innerControl.setValue(null, { emitEvent: false });
        }
      });
  }

  private isTouchedInnerControlStream(): void {
    FormHelper.isTouched(this.innerControl)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isTouched => isTouched && this.control.markAsTouched());
  }
}
